<template>
  <div v-if="editRow" ref="containerRef" @mouseup.stop @mousedown.stop>
    <UIInputSelect
      v-model="currentValue"
      :data
      size="sm"
      @ready="handleReady"
    />
  </div>
  <span v-else class="repository-collaborators-access" @click="handleClickEdit">
    <div class="repository-collaborators-access__value">
      <div>{{ displayValue || EMPTY_VALUE_PLACEHOLDER }}</div>
      <template v-if="assetsCount">
        <button
          :key="JSON.stringify(item.share_root_asset_ids)"
          v-tooltip.top="tooltip"
          type="button"
          class="ui-link"
          @click.stop="handleShareAssets"
        >{{ assetsCount }} account{{ assetsCount > 1 ? 's' : '' }}</button>
      </template>
    </div>
    <UIPreloader v-if="isLoading" />
    <PencilIcon v-else-if="isEditable" aria-hidden="true" />
  </span>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, useTemplateRef, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'

import { RepositoryCollaborator, RepositoryInvite, RepositoryStatus } from '@types'

import { ACCESS_TYPE_OWNER, ACCESS_TYPES, EMPTY_VALUE_PLACEHOLDER } from '@/const'

import { handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { useAssetsStore } from '@/store/assets'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'

import { UIPreloader } from '@ui/core'
import { UIInputSelect } from '@ui/fields'
import { PencilIcon } from '@heroicons/vue/24/outline'
import CollaboratorsFormScopeAccounts from './CollaboratorsFormScopeAccounts.vue'

type Props = {
  item: RepositoryCollaborator
  editRow: boolean
  isReadonly: boolean
  repositoryId: string
}

type Emits = {
  (e: 'edit', value?: RepositoryCollaborator): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsStore = useAssetsStore()
const modalsStore = useModalsStore()
const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()

const { success } = useNotifications()

const containerRef = useTemplateRef('containerRef')

const currentValue = ref<string>()

const isLoading = ref(false)

const displayValue = computed(() => {
  return ACCESS_TYPES[props.item.access_type - 1] || EMPTY_VALUE_PLACEHOLDER
})

const assetsCount = computed(() => props.item.share_root_asset_ids?.length)

const tooltip = computed(() => {
  return {
    content: fetchAssets,
    loadingContent: 'Loading assets...',
    html: true,
  }
})

const data = computed(() =>
  ACCESS_TYPES.filter((_, index) => index !== ACCESS_TYPE_OWNER - 1).map(
    (label, index) => ({
      value: `${index + 1}`,
      label,
    }),
  ),
)

const isEditable = computed(() => {
  return (
    !props.isReadonly &&
    props.item.access_type !== ACCESS_TYPE_OWNER &&
    props.item.status !== RepositoryStatus.PENDING
  )
})

const handleReady = (el: HTMLInputElement) => {
  el.focus()
}

const handleClickEdit = async () => {
  if (!isEditable.value) return
  currentValue.value = `${props.item.access_type}`
  emit('edit', props.item)
}

const handleUpdateAccessType = async () => {
  if (!currentValue.value) return
  const access_type = +currentValue.value
  emit('edit', undefined)
  currentValue.value = undefined
  await handleUpdate({ access_type }, () => {
    success({ message: 'Access rights have been successfully changed' })
  })
}

const updateInvite = async (id: string, data: Partial<RepositoryInvite>) => {
  try {
    await repositoriesCollaboratorsStore.updateInvite(id, data, true)
  } catch (e) {
    throw Error(e as string)
  }
}

const updateCollaborator = async (id: string, data: Partial<RepositoryCollaborator>) => {
  try {
    await repositoriesCollaboratorsStore.update(id, props.repositoryId, data)
  } catch (e) {
    throw Error(e as string)
  }
}

const handleUpdate = async (data: { share_root_asset_ids?: string[], access_type?: number }, callback?: () => void) => {
  const id = props.item.user?.id || props.item.id
  try {
    isLoading.value = true
    if (props.item.user?.id) {
      await updateCollaborator(id, data)
    } else {
      await updateInvite(id, data)
    }
    callback?.()
  } catch (e) {
    handleCatchedError(e as string, { id })
  } finally {
    isLoading.value = false
  }
}

const handleShareAssets = (e: MouseEvent) => {
  e.stopPropagation()
  const modal = modalsStore.init('collaborators-form-scope-assets', markRaw(CollaboratorsFormScopeAccounts))
  modal?.open(modalsStore.getZIndex(), {
    assetIds: props.item.share_root_asset_ids,
    repositoryId: props.repositoryId,
    onDone: (value: string[]) => {
      handleUpdate({  share_root_asset_ids: value }, () => {
        success({ message: value.length ? 'Shared accounts have been successfully changed' : 'You successfully shared the whole repository' })
      })
    },
  })
}

const fetchAssets = async () => {
  try {
    const result = await assetsStore.fetch(props.repositoryId, false, props.item.share_root_asset_ids)
    return result.filter(item => props.item.share_root_asset_ids?.includes(item.id)).map(item => item.name).join('<br />')
  }  catch (e) {
    handleCatchedError(e as string, { id: props.repositoryId })
  }
}

watch(currentValue, (value, oldValue) => {
  if (!oldValue || !value) return
  handleUpdateAccessType()
})

onClickOutside(containerRef, event => {
  const path =
    (event as any).path || (event.composedPath && event.composedPath())
  if (
    path.find((item: HTMLElement) =>
      item.classList?.contains('v-popper__popper'),
    )
  )
    return
  emit('edit', undefined)
  currentValue.value = undefined
})
</script>

<style>
.repository-collaborators-access {
  @apply flex items-center;
  @apply gap-1;

  svg {
    @apply w-3 h-3;
    @apply hidden;
    @apply hover:text-indigo-500;
  }

  &__value {
    @apply flex flex-col items-start;
  }
}

[data-column='access_type']:hover {
  .repository-collaborators-access {
    svg {
      @apply block;
    }
  }
}
</style>
